import { useEffect, useMemo, useState } from 'react';
import { useParams, usePathname, useRouter } from 'next/navigation';
import { signOut as nextAuthSignOut } from 'next-auth/react';
import type { Token } from '@/interfaces/service/auth.interface';
import { useAppStoreStore } from '@/stores/appstore.store';
import { useFavoriteStore } from '@/stores/favoriteStore.store';
import { useMembersStore } from '@/stores/members.store';
import { useOrganizationTeamStore } from '@/stores/organizationTeam.store';
import { PostReplyStore } from '@/stores/post.reply.store';
import { PAGE_NAME, PageName } from '@/constants/navigation.constants';
import { REVIEW_ROUTES, ROUTES } from '@/constants/path.constants';
import { clearAuthorization, hasClientToken } from '@/utils/auth.util';
import { getUserInfo } from '@/utils/user.util';
import { useGetSubscribedStores } from '@/libs/hooks/subscription';
import { getRole, getToken, setRole } from '@/libs/utils/authn';

export const useNavigationLogic = () => {
  const pathParams = useParams();
  const pathname = usePathname();
  const router = useRouter();
  const { organizations, teams } = useOrganizationTeamStore();
  const hasToken = hasClientToken();
  const appStoreId = Number(pathParams?.appStoreId ?? '-1');
  const [isSupervisor, setIsSupervisor] = useState(false);
  const reviewBoosterStoreIds = useMemo(() => Array.from(new Set(teams.map((team) => team.storeId).filter((storeId) => storeId > -1))), [teams]);
  const { data: reviewBoosterSubscription } = useGetSubscribedStores('REVIEW_BOOSTER', reviewBoosterStoreIds, {
    enabled: hasToken && reviewBoosterStoreIds.length > 0
  });
  const subscribedReviewBoosterStoreIds = reviewBoosterSubscription?.subscribedStoreIds ?? [];

  useEffect(() => {
    const role = getUserInfo()?.role;
    if (role) {
      setIsSupervisor(role === 'ROLE_SUPERVISOR');
    }
  }, []);

  // 컴포넌트 마운트 시 최초 한 번 role을 쿠키에 저장 (로그인 상태일 때만)
  useEffect(() => {
    if (hasToken) {
      setRole();
    }
  }, [hasToken]);

  // 사용자가 조직의 오너나 관리자인지 확인
  const isOwnerOrAdmin = organizations.some((org) => org.organizationRole === 'ORGANIZATION_OWNER' || org.organizationRole === 'ORGANIZATION_ADMIN');

  // 사용자가 관리하는 병원 중 리뷰부스터 구독중인 병원이 있는지 확인
  const hasReviewBoosterAccess = subscribedReviewBoosterStoreIds.length > 0;

  const clearStores = () => {
    useAppStoreStore.persist.clearStorage();
    PostReplyStore.persist.clearStorage();
    useOrganizationTeamStore.persist.clearStorage();
    useOrganizationTeamStore.getState().clearOrganizationTeam();
    useMembersStore.persist.clearStorage();
    useMembersStore.getState().clearMembers();
    useFavoriteStore.persist.clearStorage();
    useFavoriteStore.getState().clearFavoriteStore();
  };

  const getMediContentsPath = (): string => {
    let token: Token | null = null;
    if (typeof window !== 'undefined') {
      token = getToken();
    }

    const medicontentsUrl = process.env.NEXT_PUBLIC_MEDICONTENTS_URL || 'http://localhost:3000';

    if (getRole() === 'SUPERVISOR') {
      return `${medicontentsUrl}/admin`;
    }
    return `${medicontentsUrl}/client`;
  };

  const getReviewBoosterPath = (storeId: number) => {
    if (isSupervisor) {
      return storeId > -1 ? `/review-booster/${storeId}` : '/review-booster';
    }

    if (!hasReviewBoosterAccess) {
      return '/review-booster/landing';
    }

    if (storeId > -1 && subscribedReviewBoosterStoreIds.includes(storeId)) {
      return `/review-booster/${storeId}`;
    }

    return `/review-booster/${subscribedReviewBoosterStoreIds[0]}`;
  };

  const getNavigationItem = (pageName: PageName, _appStoreId: number): string => {
    switch (pageName) {
      case PAGE_NAME.REVIEW_BOOSTER_LANDING:
        return '/review-booster/landing';
      case PAGE_NAME.DASHBOARD:
        return _appStoreId && _appStoreId > -1 ? REVIEW_ROUTES.getStoreDashboard(_appStoreId) : ROUTES.getMainDashboard();
      case PAGE_NAME.REVIEWS:
        return _appStoreId > -1 ? REVIEW_ROUTES.getReviews(_appStoreId) : ROUTES.getMainDashboard();
      case PAGE_NAME.ORGANIZATION:
        return ROUTES.getOrganization();
      case PAGE_NAME.REVIEW_BOOSTER:
        return getReviewBoosterPath(_appStoreId);
      case PAGE_NAME.ENTERPRISE:
        return '/enterprise/landing';
      case PAGE_NAME.BLOG:
        return 'https://blog.revieworks.com/';
      case PAGE_NAME.MEDICONTENTS_LANDING:
        return '/medicontents/landing';
      case PAGE_NAME.MEDICONTENTS:
        return getMediContentsPath();
    }
  };

  const signOut = async () => {
    try {
      await nextAuthSignOut({ redirect: false });
    } catch (error) {
      // 세션이 없거나 이미 로그아웃된 경우 무시
      console.log('signOut error (ignored):', error);
    }

    // 로컬 토큰 및 스토어 정리
    clearAuthorization();
    clearStores();

    router.push(ROUTES.getLogin());
  };

  return {
    pathname,
    appStoreId,
    hasToken,
    isOwnerOrAdmin,
    getNavigationItem,
    getMediContentsPath,
    signOut
  };
};
